<script setup>
// NOTICE: KAPTION-INTEGRATION
// security-center.UI.6 — Inicio de sesión único (canvas: Main › SSO): the
// connected provider (test / edit / enable / disconnect) or the empty state,
// plus the data to paste into the provider.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import KaptionSecurityAPI from 'dashboard/api/kaptionSecurity';
import { useKaptionSecurity } from 'dashboard/composables/useKaptionSecurity';
import KaptionSecuritySection from '../components/KaptionSecuritySection.vue';
import KaptionSecurityBadge from '../components/KaptionSecurityBadge.vue';
import KaptionSecurityCopyRow from '../components/KaptionSecurityCopyRow.vue';
import KaptionSecurityConnectDialog from '../components/KaptionSecurityConnectDialog.vue';
import KaptionSecurityTestDialog from '../components/KaptionSecurityTestDialog.vue';
import { useKaptionSecurityText } from '../useKaptionSecurityText';

const { t } = useI18n();
const { overview, setOverview, connection } = useKaptionSecurity();
const { provider, formatWhen } = useKaptionSecurityText(overview);

const connectRef = ref(null);
const testRef = ref(null);
const disconnectRef = ref(null);
const busy = ref(false);

const INITIALS = {
  auth0: 'A0',
  okta: 'OK',
  entra: 'ME',
  google: 'GW',
  oidc: 'ID',
  saml: 'SA',
};

const status = computed(() => {
  const value = connection.value?.status;
  if (value === 'enabled')
    return { tone: 'teal', label: t('KAPTION_SECURITY.SSO.STATUS_ENABLED') };
  if (value === 'tested')
    return { tone: 'amber', label: t('KAPTION_SECURITY.SSO.STATUS_TESTED') };
  return { tone: 'slate', label: t('KAPTION_SECURITY.SSO.STATUS_DRAFT') };
});

const subtitle = computed(() => {
  const c = connection.value;
  if (!c) return '';
  const where =
    c.protocol === 'saml'
      ? c.saml_idp_entity_id
      : (c.issuer || '').replace(/^https?:\/\//, '').replace(/\/$/, '');
  let test = t('KAPTION_SECURITY.SSO.NEVER_TESTED');
  if (c.last_tested_at)
    test = c.last_test_ok
      ? t('KAPTION_SECURITY.SSO.LAST_TEST_OK', {
          when: formatWhen(c.last_tested_at),
        })
      : t('KAPTION_SECURITY.SSO.LAST_TEST_FAILED', {
          when: formatWhen(c.last_tested_at),
        });
  return [where, test].filter(Boolean).join(' · ');
});

const protocolName = computed(() =>
  connection.value?.protocol === 'saml'
    ? t('KAPTION_SECURITY.SSO.PROTOCOL_SAML')
    : t('KAPTION_SECURITY.SSO.PROTOCOL_OIDC')
);

const urls = computed(() => overview.value?.urls || {});

const enable = async () => {
  busy.value = true;
  try {
    const { data } = await KaptionSecurityAPI.enableConnection();
    setOverview(data);
    useAlert(t('KAPTION_SECURITY.SSO.ENABLED'));
  } catch (e) {
    useAlert(t('KAPTION_SECURITY.COMMON.ERROR'));
  } finally {
    busy.value = false;
  }
};

const disconnect = async () => {
  busy.value = true;
  try {
    const { data } = await KaptionSecurityAPI.removeConnection();
    setOverview(data);
    disconnectRef.value?.close();
    useAlert(t('KAPTION_SECURITY.SSO.DISCONNECTED'));
  } catch (e) {
    useAlert(t('KAPTION_SECURITY.COMMON.ERROR'));
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-11">
    <KaptionSecuritySection
      :title="t('KAPTION_SECURITY.SSO.TITLE')"
      :description="t('KAPTION_SECURITY.SSO.DESC')"
    >
      <div
        v-if="connection"
        class="flex flex-col gap-4 p-4 border rounded-xl border-n-weak bg-n-alpha-1 sm:flex-row sm:items-center"
        data-testid="ks-connection-card"
      >
        <div
          class="flex items-center justify-center text-[13px] font-bold rounded-[10px] size-11 shrink-0 bg-n-slate-12 text-n-background"
        >
          {{ INITIALS[connection.provider] || 'ID' }}
        </div>
        <div class="flex flex-col flex-1 min-w-0 gap-1">
          <div class="flex flex-wrap items-center gap-2">
            <span class="text-sm font-semibold text-n-slate-12">{{
              provider === protocolName
                ? provider
                : `${provider} · ${protocolName}`
            }}</span>
            <KaptionSecurityBadge :tone="status.tone" :label="status.label" />
          </div>
          <span class="text-sm truncate text-n-slate-11">{{ subtitle }}</span>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <Button
            faded
            sm
            type="button"
            :label="t('KAPTION_SECURITY.SSO.TEST')"
            data-testid="ks-sso-test"
            @click="testRef.start()"
          />
          <Button
            faded
            sm
            type="button"
            :label="t('KAPTION_SECURITY.SSO.EDIT')"
            @click="connectRef.open()"
          />
          <Button
            v-if="connection.status === 'tested'"
            sm
            type="button"
            :label="t('KAPTION_SECURITY.SSO.ENABLE')"
            :is-loading="busy"
            @click="enable"
          />
          <Button
            ghost
            ruby
            sm
            type="button"
            :label="t('KAPTION_SECURITY.SSO.DISCONNECT')"
            @click="disconnectRef.open()"
          />
        </div>
      </div>
      <div
        v-else
        class="flex flex-col gap-4 p-5 border border-dashed rounded-xl border-n-strong sm:flex-row sm:items-center"
      >
        <div class="flex flex-col flex-1 gap-1">
          <span class="text-sm font-semibold text-n-slate-12">{{
            t('KAPTION_SECURITY.SSO.EMPTY_TITLE')
          }}</span>
          <span class="text-sm text-n-slate-11">{{
            t('KAPTION_SECURITY.SSO.EMPTY_DESC')
          }}</span>
        </div>
        <Button
          type="button"
          :label="t('KAPTION_SECURITY.SSO.CONNECT')"
          data-testid="ks-sso-connect"
          @click="connectRef.open()"
        />
      </div>
    </KaptionSecuritySection>

    <KaptionSecuritySection :title="t('KAPTION_SECURITY.SSO.RUMO_DATA')">
      <div class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak">
        <KaptionSecurityCopyRow
          :label="t('KAPTION_SECURITY.CONNECT.CALLBACK')"
          :value="urls.oidc_callback || ''"
        />
        <KaptionSecurityCopyRow
          :label="t('KAPTION_SECURITY.CONNECT.LOGOUT')"
          :value="urls.logout || ''"
        />
        <KaptionSecurityCopyRow
          :label="t('KAPTION_SECURITY.CONNECT.ACS')"
          :value="urls.saml_acs || ''"
        />
        <KaptionSecurityCopyRow
          :label="t('KAPTION_SECURITY.CONNECT.ENTITY')"
          :value="urls.saml_entity_id || ''"
        />
        <KaptionSecurityCopyRow
          :label="t('KAPTION_SECURITY.CONNECT.METADATA')"
          :value="urls.saml_metadata || ''"
        />
      </div>
    </KaptionSecuritySection>

    <KaptionSecurityConnectDialog ref="connectRef" @saved="testRef.start()" />
    <KaptionSecurityTestDialog ref="testRef" @edit="connectRef.open()" />
    <Dialog
      ref="disconnectRef"
      type="alert"
      :title="t('KAPTION_SECURITY.SSO.DISCONNECT_TITLE', { provider })"
      :description="t('KAPTION_SECURITY.SSO.DISCONNECT_DESC', { provider })"
      :confirm-button-label="t('KAPTION_SECURITY.SSO.DISCONNECT')"
      :is-loading="busy"
      @confirm="disconnect"
    />
  </div>
</template>
